import { io, type Socket } from 'socket.io-client'
import { getAccessToken, refreshSession } from './apiClient'

/**
 * Socket.io-клиент для групповых чатов.
 *
 * Две правки против мобильной версии, обе обязательные для веба:
 *
 *  1. auth задан колбэком. Socket.io вызывает его перед КАЖДОЙ попыткой
 *     подключения. В мобилке auth был объектом, снятым в момент connect:
 *     при автопереподключении улетал токен, протухший ещё до разрыва.
 *
 *  2. Обработан connect_error. В socket.io v4 ошибка auth-middleware
 *     фатальна — socket.active становится false и автопереподключение
 *     не запускается вовсе. Вкладка, провисевшая в фоне дольше 15 минут
 *     (время жизни access-токена), больше не подключалась никогда.
 *
 * Транспорт websocket+polling, а не только websocket: за корпоративными
 * прокси и в части церковных сетей вебсокет не проходит.
 */

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || window.location.origin

let socket: Socket | null = null
let isRecovering = false

export function getSocket(): Socket {
  if (socket) return socket

  socket = io(SOCKET_URL, {
    path: '/socket.io',
    autoConnect: false,
    transports: ['websocket', 'polling'],
    withCredentials: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
    // Колбэк, а не объект: значение берётся заново на каждой попытке
    auth: (cb) => cb({ token: getAccessToken() }),
  })

  socket.on('connect_error', (error) => {
    const message = (error as Error).message ?? ''
    const isAuthError = /token|auth|unauthor/i.test(message)

    if (!isAuthError || isRecovering) return

    // Обновляем токен и подключаемся вручную — сам socket.io этого не сделает
    isRecovering = true
    void refreshSession()
      .then((token) => {
        if (token && socket && !socket.connected) {
          socket.connect()
        }
      })
      .finally(() => {
        isRecovering = false
      })
  })

  return socket
}

export function connectSocket(): void {
  const s = getSocket()
  if (!s.connected) {
    s.connect()
  }
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect()
  }
}

/** Полный сброс — при выходе из аккаунта, чтобы не утащить сессию в следующий вход. */
export function resetSocket(): void {
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
}
